import { useEffect, useState } from "react";
import {
  fetchAcademyContacts,
  createAcademyContact,
  updateAcademyContact,
  deleteAcademyContact,
} from "@/api/academyAuth";

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    title: "",
    url: "",
    icon: "",
    is_active: true,
  });

  const loadContacts = async () => {
    try {
      const data = await fetchAcademyContacts();
      setContacts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setContacts([]);
      setError("فشل تحميل روابط التواصل (تأكد أنك داخل كـ Academy)");
    }
  };

  useEffect(() => {
    loadContacts();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      title: "",
      url: "",
      icon: "",
      is_active: true,
    });
    setSuccess("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (editingId) {
        await updateAcademyContact(editingId, form);
        setSuccess("تم تحديث الرابط بنجاح");
      } else {
        await createAcademyContact(form);
        setSuccess("تم إضافة الرابط بنجاح");
      }
      resetForm();
      loadContacts();
    } catch (e) {
      console.error(e);
      setError("حدث خطأ أثناء الحفظ (تأكد أن الرابط صحيح)");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الرابط؟")) return;
    
    try {
      await deleteAcademyContact(id);
      setSuccess("تم حذف الرابط بنجاح");
      loadContacts();
    } catch (e) {
      console.error(e);
      setError("فشل حذف الرابط");
    }
  };

  const handleEdit = (contact) => {
    setEditingId(contact.id);
    setForm({
      title: contact.label || "",
      url: contact.url || "",
      icon: contact.icon || "",
      is_active: Boolean(contact.is_active),
    });
    setSuccess("");
    setError("");
  };

  // تنسيقات CSS
  const styles = {
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "25px 20px",
      minHeight: "calc(100vh - 160px)"
    },
    header: {
      marginBottom: "30px",
      paddingBottom: "20px",
      borderBottom: "2px solid #e9ecef"
    },
    title: {
      margin: "0 0 10px 0",
      color: "#182848",
      fontSize: "28px",
      fontWeight: "700",
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    subtitle: {
      margin: "0 0 20px 0",
      color: "#6c757d",
      fontSize: "16px"
    },
    twoColumns: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "30px",
      marginBottom: "30px"
    },
    card: {
      backgroundColor: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
      padding: "25px"
    },
    cardTitle: {
      margin: "0 0 25px 0",
      color: "#182848",
      fontSize: "20px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      paddingBottom: "15px",
      borderBottom: "1px solid #e9ecef"
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "20px"
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px"
    },
    label: {
      color: "#495057",
      fontWeight: "500",
      fontSize: "15px",
      display: "flex",
      alignItems: "center",
      gap: "5px"
    },
    required: {
      color: "#dc3545"
    },
    input: {
      padding: "14px 16px",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "16px",
      transition: "all 0.3s",
      backgroundColor: "#f8f9fa",
      boxSizing: "border-box",
      fontFamily: "inherit",
      width: "100%"
    },
    checkboxContainer: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginTop: "5px"
    },
    checkbox: {
      width: "18px",
      height: "18px",
      cursor: "pointer"
    },
    checkboxLabel: {
      color: "#495057",
      fontSize: "15px",
      cursor: "pointer"
    },
    iconPreview: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginTop: "5px"
    },
    iconExample: {
      color: "#6c757d",
      fontSize: "14px"
    },
    formButtons: {
      display: "flex",
      gap: "15px",
      marginTop: "10px"
    },
    primaryButton: {
      padding: "14px 25px",
      background: "linear-gradient(90deg, #4b6cb7 0%, #182848 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      flex: "1",
      fontFamily: "inherit"
    },
    secondaryButton: {
      padding: "14px 25px",
      backgroundColor: "#f8f9fa",
      color: "#495057",
      border: "1px solid #e1e5eb",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      flex: "1",
      fontFamily: "inherit"
    },
    buttonDisabled: {
      opacity: "0.7",
      cursor: "not-allowed"
    },
    spinner: {
      width: "20px",
      height: "20px",
      border: "2px solid rgba(255, 255, 255, 0.3)",
      borderRadius: "50%",
      borderTopColor: "white",
      animation: "spin 1s ease-in-out infinite"
    },
    successMessage: {
      backgroundColor: "#d4edda",
      border: "1px solid #c3e6cb",
      color: "#155724",
      padding: "15px",
      borderRadius: "10px",
      marginTop: "15px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "15px",
      animation: "fadeIn 0.3s ease-out"
    },
    errorMessage: {
      backgroundColor: "#f8d7da",
      border: "1px solid #f5c6cb",
      color: "#721c24",
      padding: "15px",
      borderRadius: "10px",
      marginTop: "15px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "15px",
      animation: "fadeIn 0.3s ease-out"
    },
    icon: {
      width: "20px",
      height: "20px",
      flexShrink: "0"
    },
    contactsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: "20px",
      marginTop: "10px"
    },
    contactCard: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "20px",
      border: "1px solid #e9ecef",
      transition: "all 0.3s",
      position: "relative",
      opacity: "1"
    },
    contactCardInactive: {
      opacity: "0.6",
      backgroundColor: "#f8f9fa"
    },
    contactHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "15px"
    },
    contactTitle: {
      margin: "0",
      color: "#182848",
      fontSize: "18px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "10px"
    },
    statusBadge: {
      padding: "4px 10px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: "600"
    },
    activeBadge: {
      backgroundColor: "rgba(40, 167, 69, 0.1)",
      color: "#28a745"
    },
    inactiveBadge: {
      backgroundColor: "rgba(108, 117, 125, 0.1)",
      color: "#6c757d"
    },
    contactIcon: {
      width: "24px",
      height: "24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(75, 108, 183, 0.1)",
      borderRadius: "8px",
      color: "#4b6cb7",
      fontSize: "14px",
      fontWeight: "600"
    },
    contactUrl: {
      margin: "0 0 15px 0",
      wordBreak: "break-word"
    },
    urlLink: {
      color: "#4b6cb7",
      textDecoration: "none",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "color 0.2s"
    },
    contactFooter: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "20px",
      paddingTop: "15px",
      borderTop: "1px solid #e9ecef"
    },
    iconName: {
      color: "#6c757d",
      fontSize: "13px",
      backgroundColor: "#f8f9fa",
      padding: "4px 10px",
      borderRadius: "15px"
    },
    actionButtons: {
      display: "flex",
      gap: "10px"
    },
    editButton: {
      padding: "8px 16px",
      backgroundColor: "rgba(255, 193, 7, 0.1)",
      color: "#ffc107",
      border: "1px solid rgba(255, 193, 7, 0.3)",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.3s"
    },
    deleteButton: {
      padding: "8px 16px",
      backgroundColor: "rgba(220, 53, 69, 0.1)",
      color: "#dc3545",
      border: "1px solid rgba(220, 53, 69, 0.3)",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.3s"
    },
    emptyState: {
      textAlign: "center",
      padding: "40px 20px",
      backgroundColor: "#f8f9fa",
      borderRadius: "12px",
      border: "2px dashed #dee2e6"
    },
    emptyIcon: {
      fontSize: "48px",
      color: "#adb5bd",
      marginBottom: "15px",
      opacity: "0.5"
    },
    emptyTitle: {
      margin: "0 0 10px 0",
      color: "#6c757d",
      fontSize: "18px",
      fontWeight: "600"
    },
    emptyText: {
      margin: "0",
      color: "#6c757d",
      fontSize: "14px"
    },
    statsCard: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "20px",
      boxShadow: "0 3px 10px rgba(0, 0, 0, 0.08)",
      display: "flex",
      alignItems: "center",
      gap: "15px",
      marginBottom: "30px"
    },
    statIcon: {
      width: "50px",
      height: "50px",
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: "0",
      backgroundColor: "rgba(75, 108, 183, 0.1)"
    },
    statContent: {
      flex: "1"
    },
    statTitle: {
      margin: "0 0 5px 0",
      color: "#6c757d",
      fontSize: "14px",
      fontWeight: "500"
    },
    statValue: {
      margin: "0",
      color: "#182848",
      fontSize: "24px",
      fontWeight: "700"
    }
  };

  // أيقونات SVG
  const ContactIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#4b6cb7", width: "28px", height: "28px" }}>
      <path fillRule="evenodd" d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" clipRule="evenodd" />
    </svg>
  );

  const SuccessIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#155724" }}>
      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
    </svg>
  );

  const ErrorIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ ...styles.icon, color: "#721c24" }}>
      <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
    </svg>
  );

  const LinkIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "16px", height: "16px", color: "#4b6cb7" }}>
      <path fillRule="evenodd" d="M19.902 4.098a3.75 3.75 0 00-5.304 0l-4.5 4.5a3.75 3.75 0 001.035 6.037.75.75 0 01-.646 1.353 5.25 5.25 0 01-1.449-8.45l4.5-4.5a5.25 5.25 0 117.424 7.424l-1.757 1.757a.75.75 0 11-1.06-1.06l1.757-1.757a3.75 3.75 0 000-5.304z" clipRule="evenodd" />
      <path fillRule="evenodd" d="M5.45 8.55a5.25 5.25 0 007.424 7.424l1.757-1.757a.75.75 0 011.06 1.06l-1.757 1.757a6.75 6.75 0 119.546-9.546l-4.5 4.5a6.75 6.75 0 01-9.546 0 3.75 3.75 0 010-5.304 3.75 3.75 0 015.304 0l4.5 4.5a3.75 3.75 0 01-5.304 5.304l-1.757-1.757a.75.75 0 011.06-1.06l1.757 1.757a2.25 2.25 0 003.182-3.182l-4.5-4.5a2.25 2.25 0 00-3.182 0 2.25 2.25 0 000 3.182z" clipRule="evenodd" />
    </svg>
  );

  // دالة للحصول على أيقونة من اسم الأيقونة
  const getIconDisplay = (iconName) => {
    const icons = {
      whatsapp: "📱",
      phone: "📞",
      telegram: "✈️",
      email: "📧",
      website: "🌐",
      facebook: "📘",
      twitter: "🐦",
      instagram: "📷",
      youtube: "▶️",
      linkedin: "💼",
      default: "🔗"
    };
    
    return icons[iconName?.toLowerCase()] || icons.default;
  };

  // تأثيرات hover
  const handleInputFocus = (e) => {
    e.target.style.outline = "none";
    e.target.style.borderColor = "#4b6cb7";
    e.target.style.boxShadow = "0 0 0 3px rgba(75, 108, 183, 0.1)";
    e.target.style.backgroundColor = "white";
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = "#e1e5eb";
    e.target.style.boxShadow = "none";
    e.target.style.backgroundColor = "#f8f9fa";
  };

  const handleButtonHover = (e, type = "primary") => {
    if (type === "primary") {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 7px 14px rgba(75, 108, 183, 0.2)";
    } else if (type === "edit") {
      e.currentTarget.style.backgroundColor = "rgba(255, 193, 7, 0.2)";
      e.currentTarget.style.transform = "translateY(-1px)";
    } else if (type === "delete") {
      e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.2)";
      e.currentTarget.style.transform = "translateY(-1px)";
    }
  };

  const handleButtonLeave = (e, type = "primary") => {
    if (type === "primary") {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    } else if (type === "edit") {
      e.currentTarget.style.backgroundColor = "rgba(255, 193, 7, 0.1)";
      e.currentTarget.style.transform = "translateY(0)";
    } else if (type === "delete") {
      e.currentTarget.style.backgroundColor = "rgba(220, 53, 69, 0.1)";
      e.currentTarget.style.transform = "translateY(0)";
    }
  };

  const handleCardHover = (e, isActive) => {
    if (isActive) {
      e.currentTarget.style.transform = "translateY(-5px)";
      e.currentTarget.style.boxShadow = "0 10px 25px rgba(0, 0, 0, 0.1)";
      e.currentTarget.style.borderColor = "#4b6cb7";
    }
  };

  const handleCardLeave = (e, isActive) => {
    if (isActive) {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 0 0 rgba(0, 0, 0, 0)";
      e.currentTarget.style.borderColor = "#e9ecef";
    }
  };

  const handleLinkHover = (e) => {
    e.currentTarget.style.color = "#182848";
  };

  const handleLinkLeave = (e) => {
    e.currentTarget.style.color = "#4b6cb7";
  };

  return (
    <div style={styles.container}>
      {/* رأس الصفحة */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <ContactIcon />
          إدارة روابط التواصل
        </h1>
        <p style={styles.subtitle}>قم بإضافة وتعديل روابط التواصل الخاصة بأكاديميتك</p>
      </div>

      {/* إحصائيات سريعة */}
      <div style={styles.statsCard}>
        <div style={styles.statIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4b6cb7" style={{ width: "24px", height: "24px" }}>
            <path fillRule="evenodd" d="M19.902 4.098a3.75 3.75 0 00-5.304 0l-4.5 4.5a3.75 3.75 0 001.035 6.037.75.75 0 01-.646 1.353 5.25 5.25 0 01-1.449-8.45l4.5-4.5a5.25 5.25 0 117.424 7.424l-1.757 1.757a.75.75 0 11-1.06-1.06l1.757-1.757a3.75 3.75 0 000-5.304z" clipRule="evenodd" />
          </svg>
        </div>
        <div style={styles.statContent}>
          <h3 style={styles.statTitle}>إجمالي روابط التواصل</h3>
          <p style={styles.statValue}>{contacts.length}</p>
        </div>
      </div>

      {/* محتوى الصفحة في عمودين */}
      <div style={styles.twoColumns}>
        {/* العمود الأيسر: نموذج الإضافة/التعديل */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            {editingId ? "تعديل الرابط" : "إضافة رابط جديد"}
          </h2>
          
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                عنوان الرابط
                <span style={styles.required}>*</span>
              </label>
              <input
                placeholder="مثال: واتساب، تليجرام، الموقع الرسمي"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                رابط التواصل
                <span style={styles.required}>*</span>
              </label>
              <input
                placeholder="https://example.com أو رقم هاتف"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                اسم الأيقونة
              </label>
              <input
                placeholder="whatsapp, telegram, phone, email, website"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={styles.input}
              />
              {form.icon && (
                <div style={styles.iconPreview}>
                  <span>معاينة الأيقونة:</span>
                  <span style={styles.contactIcon}>{getIconDisplay(form.icon)}</span>
                  <span style={styles.iconExample}>{form.icon}</span>
                </div>
              )}
            </div>

            <div style={styles.formGroup}>
              <div style={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  style={styles.checkbox}
                />
                <label htmlFor="is_active" style={styles.checkboxLabel}>
                  رابط نشط
                </label>
              </div>
            </div>

            <div style={styles.formButtons}>
              <button 
                type="submit" 
                style={{
                  ...styles.primaryButton,
                  ...(loading ? styles.buttonDisabled : {})
                }}
                disabled={loading}
                onMouseEnter={(e) => handleButtonHover(e, "primary")}
                onMouseLeave={(e) => handleButtonLeave(e, "primary")}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner}></div>
                    جاري الحفظ...
                  </>
                ) : editingId ? (
                  "تحديث الرابط"
                ) : (
                  "إضافة الرابط"
                )}
              </button>

              {editingId && (
                <button 
                  type="button" 
                  onClick={resetForm}
                  style={styles.secondaryButton}
                  onMouseEnter={(e) => handleButtonHover(e, "secondary")}
                  onMouseLeave={(e) => handleButtonLeave(e, "secondary")}
                >
                  إلغاء التعديل
                </button>
              )}
            </div>

            {/* رسائل النجاح والخطأ */}
            {success && (
              <div style={styles.successMessage}>
                <SuccessIcon />
                {success}
              </div>
            )}

            {error && (
              <div style={styles.errorMessage}>
                <ErrorIcon />
                {error}
              </div>
            )}
          </form>
        </div>

        {/* العمود الأيمن: قائمة الروابط */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            روابط التواصل الحالية ({contacts.length})
          </h2>
          
          {contacts.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🔗</div>
              <h3 style={styles.emptyTitle}>لا توجد روابط تواصل</h3>
              <p style={styles.emptyText}>قم بإضافة رابط تواصل جديد لتبدأ</p>
            </div>
          ) : (
            <div style={styles.contactsGrid}>
              {contacts.map((contact) => {
                const isActive = Boolean(contact.is_active);
                return (
                  <div 
                    key={contact.id}
                    style={{
                      ...styles.contactCard,
                      ...(!isActive ? styles.contactCardInactive : {})
                    }}
                    onMouseEnter={(e) => handleCardHover(e, isActive)}
                    onMouseLeave={(e) => handleCardLeave(e, isActive)}
                  >
                    <div style={styles.contactHeader}>
                      <h3 style={styles.contactTitle}>
                        <span style={styles.contactIcon}>
                          {getIconDisplay(contact.icon)}
                        </span>
                        {contact.label || "بدون عنوان"}
                      </h3>
                      <span style={{
                        ...styles.statusBadge,
                        ...(isActive ? styles.activeBadge : styles.inactiveBadge)
                      }}>
                        {isActive ? "نشط" : "غير نشط"}
                      </span>
                    </div>
                    
                    <p style={styles.contactUrl}>
                      <a 
                        href={contact.url} 
                        target="_blank" 
                        rel="noreferrer"
                        style={styles.urlLink}
                        onMouseEnter={handleLinkHover}
                        onMouseLeave={handleLinkLeave}
                      >
                        <LinkIcon />
                        {contact.url.length > 40 ? `${contact.url.substring(0, 40)}...` : contact.url}
                      </a>
                    </p>
                    
                    <div style={styles.contactFooter}>
                      {contact.icon && (
                        <span style={styles.iconName}>{contact.icon}</span>
                      )}
                      
                      <div style={styles.actionButtons}>
                        <button 
                          onClick={() => handleEdit(contact)}
                          style={styles.editButton}
                          onMouseEnter={(e) => handleButtonHover(e, "edit")}
                          onMouseLeave={(e) => handleButtonLeave(e, "edit")}
                        >
                          تعديل
                        </button>
                        
                        <button 
                          onClick={() => handleDelete(contact.id)}
                          style={styles.deleteButton}
                          onMouseEnter={(e) => handleButtonHover(e, "delete")}
                          onMouseLeave={(e) => handleButtonLeave(e, "delete")}
                        >
                          حذف
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* إضافة الأنيميشن */}
      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .contact-card {
          animation: fadeIn 0.3s ease-out;
        }
        
        @media (max-width: 992px) {
          .two-columns {
            grid-template-columns: 1fr !important;
          }
        }
        
        @media (max-width: 768px) {
          .contacts-grid {
            grid-template-columns: 1fr !important;
          }
          
          .card {
            padding: 20px !important;
          }
          
          .form-buttons {
            flex-direction: column !important;
          }
        }
      `}</style>
    </div>
  );
}